import { useEffect, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CrawledPage, CrawledPages, KnowledgeSource, PageSkipReason } from '../../types';
import { webSourceApi } from '../../services/webSources';

/**
 * C1: which pages a crawl actually put into the knowledge base.
 *
 * A slide-over rather than an inline expansion, because the list runs to hundreds of
 * rows on a real site and expanding in place would push the rest of the tab off screen.
 *
 * The list is reconstructed from per-document ingestion logs — Bedrock has no API that
 * will enumerate a web data source — and those logs expire after 30 days. So an empty
 * result is genuinely ambiguous, and the two cases are worded differently: a crawl that
 * indexed nothing is a problem to fix, a crawl too old to have records is not.
 *
 * IT LISTS URLS REACHED, NOT PAGES KEPT, and the difference is the whole reason this
 * panel is worth opening. A crawl records every URL it refused alongside every page it
 * indexed, and the refusals are routinely the larger half — one section-scoped crawl
 * scanned 42 and indexed 1. Until the two were separated they rendered identically, so
 * the panel contradicted the page count on the row that opened it and the customer had
 * no way to tell which number was lying.
 */

/**
 * Our words for the three refusals AWS reports.
 *
 * `unsupported` is deliberately vague, because AWS's message is: *"This URL is not
 * supported for crawling."* — and it does not say why. It was first labelled "Not a
 * readable file type", which invented a cause the message never claims; the two URLs
 * that carried it on the first real site were `instagram.com` links, ordinary HTML and
 * not files at all. Do not put a reason here that only AWS could know.
 */
const SKIP_LABELS: Record<PageSkipReason, string> = {
  out_of_scope: 'Outside crawl scope',
  blocked_by_robots: 'Blocked by robots.txt',
  unsupported: 'Could not be crawled',
  other: 'Not added',
};

const SKIP_EXPLANATIONS: Record<PageSkipReason, string> = {
  out_of_scope: 'Found on the site, but outside the part of it you pointed us at.',
  blocked_by_robots: "This site's robots.txt file asks crawlers not to read these.",
  unsupported: 'The crawler reports this link as not supported for crawling.',
  other: 'The crawler declined these.',
};

/**
 * Is this URL part of the customer's own site?
 *
 * WE decide this, from the URL — deliberately not from AWS's reason, which does not
 * carry the distinction. Measured across three real crawls: 30 of the 54 "outside the
 * crawl scope" URLs were the customer's OWN pages and 24 were other people's, while a
 * link to another site could arrive labelled out-of-scope, robots-blocked or
 * unsupported depending on the host. So AWS's three messages cut across the one line
 * that decides whether the customer can do anything: their page, or somebody else's.
 *
 * `www.` is stripped on both sides, and a subdomain of the seed host counts as the same
 * site — `blog.example.com` is theirs, and a wider scope would read it.
 */
function isSameSite(url: string, seedHost: string): boolean {
  const bare = (host: string) => host.replace(/^www\./i, '').toLowerCase();
  try {
    const host = bare(new URL(url).host);
    const seed = bare(seedHost);
    return !!seed && (host === seed || host.endsWith(`.${seed}`));
  } catch {
    return false;
  }
}

export default function CrawledPagesPanel({
  chatbotId,
  source,
  onClose,
}: {
  chatbotId: string;
  source: KnowledgeSource;
  onClose: () => void;
}) {
  const [data, setData] = useState<CrawledPages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await webSourceApi.listPages(chatbotId, source.id);
      if (cancelled) return;
      if (res.success && res.data) setData(res.data);
      else setError(res.error || 'Could not load the page list');
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [chatbotId, source.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const matches = (p: CrawledPage) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return p.url.toLowerCase().includes(q) || (p.title ?? '').toLowerCase().includes(q);
  };

  const all = data?.pages ?? [];
  const pages = all.filter(matches);
  // `outcome` is absent on a response from before it shipped. Treating that as `added`
  // keeps an old response rendering as it always did rather than filing every page
  // under a refusal heading it knows nothing about.
  const added = pages.filter(p => (p.outcome ?? 'added') === 'added');
  const withheld = pages.filter(p => p.outcome === 'skipped' || p.outcome === 'failed');

  // Counted over the whole crawl by the API, so these stay honest when the list is cut.
  const counts = data?.counts;
  const totalReached = counts ? counts.added + counts.skipped + counts.failed : all.length;

  // WHERE the rest went, and it has to account for ALL of them.
  //
  // Grouped by what the customer can act on rather than by which of AWS's three
  // messages came back. Only one of these buckets is fixable by a setting, and it is
  // the one their own pages land in; a link to another site is not readable by any
  // scope, whichever reason AWS gave for declining it.
  //
  // Every bucket is a row and the rows sum to the shortfall — the same rule C14 put on
  // the rejection breakdown. Counted off the list, so it is shown only when the list is
  // complete; a truncated list would produce rows that quietly do not add up.
  const seedHost = source.name;
  const mine = (p: CrawledPage) => isSameSite(p.url, seedHost);
  const withheldAll = all.filter(p => p.outcome === 'skipped' || p.outcome === 'failed');
  const mineWithheld = withheld.filter(mine);
  const elsewhere = withheld.filter(p => !mine(p));

  // Why they were left out, in the customer's own terms: the labels below are the
  // exact wording of the choices in AddWebsiteModal's SCOPE_CHOICES. Only offered when
  // the scope is one that can actually be widened — with "The entire site" already
  // chosen there is nothing to say, and something else refused those pages.
  //
  // "Remove and add again" is the real instruction today, not a simplification: crawl
  // settings cannot be edited after a website is added (F14). When that changes, this
  // sentence changes with it.
  // Written across several source lines and rendered as one sentence. The browser would
  // collapse the newlines anyway; doing it here means the string is the same whether it
  // ends up in text, a title attribute or a test assertion.
  const oneLine = (text: string) => text.replace(/\s+/g, ' ').trim();
  const seedPath = (() => {
    try {
      return new URL(source.website?.config.url || '').pathname;
    } catch {
      return '';
    }
  })();
  const scope = source.website?.config.scope;
  const scopeExplanation =
    mineWithheld.some(p => p.skipReason === 'out_of_scope') && (scope === 'page' || scope === 'section')
      ? scope === 'page'
        ? oneLine(`You chose “Just this page”, so we read that one address and nothing else.
           To add the rest, remove this website and add ${seedHost} with “The entire site”.`)
        : oneLine(`You chose “This section and everything under it”, so we read
           ${seedPath || 'that address'} and the pages below it. To add the rest, remove
           this website and add ${seedHost} with “The entire site”.`)
      : '';

  const buckets: { label: string; count: number }[] = [];
  if (!data?.truncated) {
    const ours = withheldAll.filter(mine);
    const outOfScope = ours.filter(p => p.skipReason === 'out_of_scope').length;
    const otherReasons = ours.length - outOfScope;
    if (outOfScope) {
      // "outside the crawl scope" is the same jargon the section copy just dropped:
      // scope is a word from the connector's API, not from anything the customer read.
      buckets.push({ label: 'On your site, outside what you asked for', count: outOfScope });
    }
    if (otherReasons) {
      buckets.push({ label: 'On your site, could not be read', count: otherReasons });
    }
    const away = withheldAll.length - ours.length;
    if (away) buckets.push({ label: 'On other websites', count: away });
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-900/30" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label={`Pages indexed from ${source.name}`}
        className="relative flex h-full w-full max-w-xl flex-col bg-white dark:bg-slate-800 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Pages we read</h3>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={source.detail}>
              {source.name}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!loading && !error && all.length > 0 && (
          <div className="border-b border-gray-200 dark:border-slate-700 px-6 py-3">
            {/* The count that answers the question the row raised, in one line and
                before the filter box: N of M, where M is every URL the crawl reached. */}
            <p className="mb-3 text-sm text-gray-900 dark:text-gray-100">
              <span className="font-medium tabular-nums">
                {counts ? counts.added : added.length} of {totalReached}
              </span>{' '}
              {totalReached === 1 ? 'page found was' : 'pages found were'} added to your chatbot
              {data!.truncated && <span className="text-gray-400 dark:text-gray-500"> · showing the first batch</span>}
            </p>

            {/* The shortfall, itemised. Every reason is a row and the rows sum to the
                number in the line above — so "1 of 42" is answered here rather than
                left to be reconstructed from the list. */}
            {buckets.length > 0 && (
              <div className="mb-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/40">
                <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                  Why the other{' '}
                  {counts ? counts.skipped + counts.failed : withheldAll.length}{' '}
                  {(counts ? counts.skipped + counts.failed : withheldAll.length) === 1
                    ? 'was'
                    : 'were'}{' '}
                  not added
                </p>
                <dl className="space-y-0.5">
                  {buckets.map(r => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3">
                      <dt className="truncate text-xs text-gray-600 dark:text-gray-400">{r.label}</dt>
                      <dd className="flex-shrink-0 text-xs tabular-nums text-gray-600 dark:text-gray-400">
                        {r.count}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Filter by URL or title"
                className="block w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:ring-0 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-100"
              />
            </div>
            {query.trim() && (
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                {pages.length} of {all.length} shown
              </p>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

          {!loading && error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {!loading && !error && data && all.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {data.reason === 'not_crawled' ? (
                <p>This website has not been read yet. Press Update knowledge to start.</p>
              ) : (
                <>
                  <p>No page records for this website.</p>
                  <p className="mt-2 text-xs">
                    The list is built from indexing records kept for{' '}
                    {data.retentionDays ?? 30} days, so a site read longer ago than that
                    has none — which is not the same as having indexed nothing. Re-read
                    the website to rebuild the list.
                  </p>
                </>
              )}
            </div>
          )}

          {!loading && !error && all.length > 0 && pages.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nothing matches that filter.</p>
          )}

          {added.length > 0 && (
            <section>
              <h4 className="mb-2 flex items-baseline gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                In your chatbot
                <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                  {added.length}
                </span>
              </h4>
              <ul className="divide-y divide-gray-100 dark:divide-slate-700/60">
                {added.map(page => (
                  <PageItem key={page.url} page={page} />
                ))}
              </ul>
            </section>
          )}

          {mineWithheld.length > 0 && (
            <section className={added.length > 0 ? 'mt-6' : undefined}>
              <h4 className="mb-1 flex items-baseline gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                On your site, not added
                <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                  {mineWithheld.length}
                </span>
              </h4>
              {/* The only bucket a setting can move — so it says WHICH setting, in the
                  words the customer chose it by. "A wider crawl scope" is our
                  vocabulary and means nothing to the person reading: they picked a
                  labelled option in a dialog, and naming that option back to them is
                  the difference between a fact and an instruction they can follow. */}
              {/* No restatement of the heading — it already says these are pages on
                  their site that were not added. What is left is why, and the fix. */}
              {scopeExplanation && (
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{scopeExplanation}</p>
              )}
              {!scopeExplanation && (
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Pages on {seedHost} we found but did not read.
                </p>
              )}
              <ul className="divide-y divide-gray-100 dark:divide-slate-700/60">
                {mineWithheld.map(page => (
                  <PageItem key={page.url} page={page} />
                ))}
              </ul>
            </section>
          )}

          {elsewhere.length > 0 && (
            <section className={added.length > 0 || mineWithheld.length > 0 ? 'mt-6' : undefined}>
              {/* "not added" is redundant here in a way it is not in the heading above
                  — nothing off the customer's site is ever a candidate for adding — but
                  three headings scanned together should read as one family, and the
                  reader cannot see why one of them carries a qualifier and one does
                  not. */}
              <h4 className="mb-1 flex items-baseline gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                On other websites, not added
                <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                  {elsewhere.length}
                </span>
              </h4>
              {/* Not fixable by any setting, which is the whole reason these are their
                  own group: a crawl reads one site. The reason chip stays as detail,
                  because AWS's three messages land on these almost interchangeably —
                  the same kind of social link came back out-of-scope on one host,
                  robots-blocked on another and unsupported on a third. */}
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Links to other websites. We only read the site you added.
              </p>
              <ul className="divide-y divide-gray-100 dark:divide-slate-700/60">
                {elsewhere.map(page => (
                  <PageItem key={page.url} page={page} />
                ))}
              </ul>
            </section>
          )}

        </div>
      </div>
    </div>
  );
}

/**
 * One URL, as a row: what it is on the left, what happened to it on the right.
 *
 * The reason used to sit UNDER the URL, which made every entry three stacked lines and
 * left the reader re-reading each one to see whether it changed. In its own column the
 * reasons line up and the eye can run down them — which is the actual question being
 * asked of this list, since one refused URL is never interesting on its own.
 *
 * The outcome is always a chip, never the absence of one.
 */
function PageItem({ page }: { page: CrawledPage }) {
  const outcome = page.outcome ?? 'added';
  const reason = page.skipReason ?? undefined;

  return (
    <li className="flex items-baseline gap-3 py-2">
      <div className="min-w-0 flex-1">
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer"
          className={`block truncate text-sm hover:underline ${
            outcome === 'added'
              ? 'text-gray-900 dark:text-gray-100'
              : 'text-gray-500 dark:text-gray-400'
          }`}
          title={page.url}
        >
          {page.title || page.url}
        </a>
        <div className="truncate text-xs text-gray-400 dark:text-gray-500">{page.url}</div>
        {/* Only for a reason we have no words of our own for — everything else is
            said once, in the column beside it. */}
        {outcome === 'skipped' && reason === 'other' && page.skipMessage && (
          <div className="truncate text-xs text-gray-400 dark:text-gray-500">{page.skipMessage}</div>
        )}
      </div>

      <div className="w-40 flex-shrink-0 text-right">
        {outcome === 'skipped' && (
          <span
            title={SKIP_EXPLANATIONS[reason ?? 'other']}
            className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-slate-700 dark:text-gray-300"
          >
            {SKIP_LABELS[reason ?? 'other']}
          </span>
        )}
        {outcome === 'failed' && (
          <span
            title={page.errorMessage || undefined}
            className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
          >
            Could not be read
          </span>
        )}
      </div>
    </li>
  );
}
